// User roles with strict permission hierarchy
export type PlatformRole = 'developer' | 'maintainer' | 'admin';

export type DatabaseType = 'mysql' | 'mssql' | 'postgres';

export const DATABASE_TYPES: readonly { value: DatabaseType; label: string }[] = [
  { value: 'mysql', label: 'MySQL' },
  { value: 'mssql', label: 'MSSQL' },
  { value: 'postgres', label: 'PostgreSQL' },
];

export function isSupportedDatabaseType(value: unknown): value is DatabaseType {
  return DATABASE_TYPES.some((db) => db.value === value);
}

/** RDS PostgreSQL major versions RPT provisions; the targets run the client's own major version. */
export const POSTGRES_MAJOR_VERSIONS = [14, 15, 16, 17, 18] as const;

/** A PostgreSQL project uses one schema; this is the default. */
export const DEFAULT_POSTGRES_SCHEMA = 'public';

/** PostgreSQL settings sent with a postgres project; omitted for other engines. */
export interface PostgresSettings {
  engineMajorVersion: number;
  schemaName: string;
}

/** From BUCKET_DETAILS.provision_status (Lambda: pending; ECS: in_progress → completed | failed). */
export type ProjectProvisionStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed';

const PROVISION_STATUSES: ProjectProvisionStatus[] = [
  'pending',
  'in_progress',
  'completed',
  'failed',
];

export function normalizeProjectProvisionStatus(
  raw?: ProjectProvisionStatus | string | null
): ProjectProvisionStatus | undefined {
  if (raw && PROVISION_STATUSES.includes(raw as ProjectProvisionStatus)) {
    return raw as ProjectProvisionStatus;
  }
  return undefined;
}

export function isProjectInfrastructureReady(
  status?: ProjectProvisionStatus | string | null
): boolean {
  const normalized = normalizeProjectProvisionStatus(status);
  // No BUCKET_DETAILS row or no provision_status on the row.
  if (normalized === undefined) return true;
  return normalized === 'completed';
}

/** True while Lambda (pending) or ECS (in_progress) is provisioning. */
export function isProvisioningActive(
  status?: ProjectProvisionStatus | string | null
): boolean {
  const normalized = normalizeProjectProvisionStatus(status);
  return normalized === 'pending' || normalized === 'in_progress';
}

export function getInfrastructureNotice(
  status: ProjectProvisionStatus,
  provisionError?: string | null
): {
  title: string;
  description: string;
} {
  switch (status) {
    case 'in_progress':
      return {
        title: 'Provisioning in progress',
        description:
          'S3 storage and database resources are being created. This usually takes several minutes. This page will refresh automatically.',
      };
    case 'failed': {
      const detail = provisionError?.trim();
      return {
        title: 'Provisioning failed',
        description: detail
          ? `${detail} Contact your platform administrator if this persists.`
          : 'Cloud resource setup did not complete. Contact your platform administrator before starting runs.',
      };
    }
    case 'pending':
      return {
        title: 'Provisioning queued',
        description:
          'Infrastructure setup is starting. Performance runs will be available once provisioning completes.',
      };
    default:
      return {
        title: 'Infrastructure not ready',
        description: 'Performance runs are unavailable until setup completes.',
      };
  }
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  organization: string;
  activeOrganizationId?: number;
  isPlatformAdmin?: boolean;
  /** Effective role for permissions/UI (Admin, Maintainer, or Developer). */
  platformRole: PlatformRole;
  memberships?: UserOrganizationMembership[];
  createdAt: Date;
  updatedAt: Date;
}

export interface UserOrganizationMembership {
  organizationId: number;
  organizationName: string;
  orgRole: PlatformRole;
}

export interface Project {
  id: string;
  name: string;
  organization: string;
  databaseType?: DatabaseType | string;
  provisionStatus?: ProjectProvisionStatus;
  provisionError?: string;
  assignedUsers: string[];
  startDate: Date;
  endDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Organization {
  id: string;
  name: string;
  type?: string;
  createdAt: Date;
}

// Role scope definitions
export const ROLE_SCOPES = {
  developer: 'project',
  maintainer: 'organization',
  admin: 'platform',
} as const;

// Permission matrix based on role hierarchy
// - Developer: view assigned projects, edit own profile (last name, phone)
// - Maintainer: Developer perms + manage project membership + update project
//   fields within their org + edit org members' contact info and org role
//   (Maintainer/Developer only). Cannot change organization or grant Admin.
// - Admin: Maintainer perms + create projects + create users + update user
//   attributes (org, platform role)
export const ROLE_PERMISSIONS = {
  developer: {
    scope: 'project',
    canViewAssignedProjects: true,
    canViewAllOrgProjects: false,
    canEditOwnProfile: true,
    canCreateProjects: false,
    canCreateUsers: false,
    canAssignUsersToProjects: false,
    canUpdateProjectFields: false,
    canUpdateUserDetails: false,
    canEditOrgUserContact: false,
  },
  maintainer: {
    scope: 'organization',
    canViewAssignedProjects: true,
    canViewAllOrgProjects: true,
    canEditOwnProfile: true,
    canCreateProjects: true,
    canCreateUsers: true,
    canAssignUsersToProjects: true,
    canUpdateProjectFields: true,
    canUpdateUserDetails: false,
    canEditOrgUserContact: true,
  },
  admin: {
    scope: 'platform',
    canViewAssignedProjects: true,
    canViewAllOrgProjects: true,
    canEditOwnProfile: true,
    canCreateProjects: true,
    canCreateUsers: true,
    canAssignUsersToProjects: true,
    canUpdateProjectFields: true,
    canUpdateUserDetails: true,
    canEditOrgUserContact: true,
  },
} as const;

export function canCreateProjects(role: PlatformRole): boolean {
  return ROLE_PERMISSIONS[role].canCreateProjects;
}

export function canCreateUsers(role: PlatformRole): boolean {
  return ROLE_PERMISSIONS[role].canCreateUsers;
}

export function canAssignUsers(role: PlatformRole): boolean {
  return ROLE_PERMISSIONS[role].canAssignUsersToProjects;
}

export function canUpdateProjectFields(role: PlatformRole): boolean {
  return ROLE_PERMISSIONS[role].canUpdateProjectFields;
}

export function canUpdateUserDetails(role: PlatformRole): boolean {
  return ROLE_PERMISSIONS[role].canUpdateUserDetails;
}

// Whether the role can edit OTHER users (limited fields for Maintainer,
// full set for Admin). Used to decide whether the Edit User flow shows
// the user picker (Admin / Maintainer) or skips straight to self-edit
// (Developer).
export function canEditOrgUsers(role: PlatformRole): boolean {
  return ROLE_PERMISSIONS[role].canEditOrgUserContact;
}

export function isPlatformAdminUser(
  user: Pick<User, 'platformRole' | 'isPlatformAdmin'> | null | undefined
): boolean {
  return Boolean(user?.isPlatformAdmin || user?.platformRole === 'admin');
}

/** Signed-in user with no platform Admin role and zero org_users memberships. */
export function isOrphanOrgUser(
  user: Pick<User, 'isPlatformAdmin' | 'platformRole'> | null | undefined,
  memberships: UserOrganizationMembership[]
): boolean {
  if (!user || isPlatformAdminUser(user)) return false;
  return memberships.length === 0;
}

const PLATFORM_ROLE_LABELS: Record<PlatformRole, string> = {
  admin: 'Admin',
  developer: 'Developer',
  maintainer: 'Maintainer',
};

/** User-facing role label (e.g. admin → Admin, not "platform admin"). */
export function formatPlatformRoleLabel(
  role: PlatformRole | string | null | undefined
): string {
  if (!role) return '';
  const key = String(role).toLowerCase() as PlatformRole;
  return PLATFORM_ROLE_LABELS[key] ?? String(role);
}

const PERSON_NAME_REGEX = new RegExp(
  "^[\\p{L}]+(?:['\\u2019\\u00b7-][\\p{L}]+)*(?: [\\p{L}]+(?:['\\u2019\\u00b7-][\\p{L}]+)*)*$",
  'u',
);

/** Validates an optional person name; empty is allowed. */
export function validatePersonName(
  value: string | null | undefined
): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  if (!PERSON_NAME_REGEX.test(trimmed)) {
    return 'Use letters, spaces, hyphens, or apostrophes only';
  }
  return null;
}

/**
 * Validates optional phone input using the same rules as the backend
 * (`types_util/user.py`): allowed formatting chars, 7-15 digits (E.164).
 */
export function validatePhoneNumber(
  value: string | null | undefined
): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;

  const formatRe = /^\+?[0-9\s\-()]{7,25}$/;
  if (!formatRe.test(trimmed)) {
    return 'Please enter a valid phone number (7-15 digits)';
  }

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) {
    return 'Please enter a valid phone number (7-15 digits)';
  }

  return null;
}

/**
 * Max length for organization and project display names stored in the database.
 * AWS resource identifiers are derived separately and truncated to 63 chars in provisioning.
 */
export const MAX_ORG_PROJECT_NAME_LENGTH = 128;

export const ORG_PROJECT_NAME_LENGTH_HINT = `Maximum ${MAX_ORG_PROJECT_NAME_LENGTH} characters`;

export function isOrgOrProjectNameTooLong(value: string | null | undefined): boolean {
  return (value ?? '').trim().length > MAX_ORG_PROJECT_NAME_LENGTH;
}

export function orgOrProjectNameLengthMessage(fieldLabel: string): string {
  return `${fieldLabel} must be at most ${MAX_ORG_PROJECT_NAME_LENGTH} characters`;
}

/** Max characters for compact UI labels (header switcher, scoped titles, etc.). */
export const COMPACT_DISPLAY_NAME_MAX_CHARS = 20;

export function truncateDisplayName(
  value: string | null | undefined,
  maxChars: number = COMPACT_DISPLAY_NAME_MAX_CHARS
): { display: string; full: string; truncated: boolean } {
  const full = (value ?? '').trim();
  if (!full) {
    return { display: '', full: '', truncated: false };
  }
  if (full.length <= maxChars) {
    return { display: full, full, truncated: false };
  }
  return {
    display: `${full.slice(0, maxChars)}…`,
    full,
    truncated: true,
  };
}

/** Compact "organization - project" label; each side truncated independently. */
export function formatOrgProjectScopeLabel(
  organizationName: string | null | undefined,
  projectName: string | null | undefined,
  options?: { separator?: string; fallback?: string }
): { display: string; full: string; truncated: boolean } {
  const separator = options?.separator ?? ' - ';
  const fallback = options?.fallback ?? 'Project runs';
  const org = truncateDisplayName(organizationName);
  const project = truncateDisplayName(projectName);

  if (!org.full && !project.full) {
    return { display: fallback, full: fallback, truncated: false };
  }
  if (!org.full) {
    return {
      display: project.display,
      full: project.full,
      truncated: project.truncated,
    };
  }
  if (!project.full) {
    return {
      display: org.display,
      full: org.full,
      truncated: org.truncated,
    };
  }

  const full = `${org.full}${separator}${project.full}`;
  const display = `${org.display}${separator}${project.display}`;
  return {
    display,
    full,
    truncated: org.truncated || project.truncated,
  };
}

/** Full display name for lists and headings; falls back when both names are empty. */
export function formatUserDisplayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback = 'User'
): string {
  const parts = [firstName?.trim(), lastName?.trim()].filter(
    (part): part is string => Boolean(part)
  );
  return parts.length > 0 ? parts.join(' ') : fallback;
}

/** Short name for welcome messages; prefers first name, then last, then fallback. */
export function formatWelcomeName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  fallback = 'User'
): string {
  const first = firstName?.trim();
  if (first) return first;
  const last = lastName?.trim();
  if (last) return last;
  return fallback;
}

/**
 * Returns a 1–2 char uppercase avatar label for a user. Defensive against
 * empty strings (federated / incomplete records can have firstName === "")
 * so `""[0]` yielding undefined doesn't render blank or blow up downstream.
 * Falls back to "U" (User) when there's nothing usable.
 */
export function getUserInitials(
  firstName: string | undefined | null,
  lastName: string | undefined | null,
): string {
  const first = firstName?.[0]?.toUpperCase() ?? '';
  const last = lastName?.[0]?.toUpperCase() ?? '';
  return first + last || 'U';
}

/** Org-scoped role for a user; null when they are not a member of that org. */
export function orgRoleForUserInOrganization(
  user: User,
  orgName: string,
): PlatformRole | null {
  const key = orgName.trim().toLowerCase();
  if (!key || isPlatformAdminUser(user)) return null;

  for (const membership of user.memberships ?? []) {
    if (membership.organizationName.trim().toLowerCase() === key) {
      return membership.orgRole;
    }
  }

  if (user.organization.trim().toLowerCase() === key) {
    return user.platformRole === 'admin' ? null : user.platformRole;
  }
  return null;
}

/** One selectable row in Edit User — a user may appear once per org membership. */
export interface UserMembershipRow {
  rowKey: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  organizationId: number | null;
  organizationName: string;
  orgRole: PlatformRole;
  isPlatformAdmin: boolean;
}

export function expandUsersToMembershipRows(
  users: User[],
  options?: {
    /** When set (Maintainer view), only rows for this organization are returned. */
    restrictToOrganizationId?: number | null;
  }
): UserMembershipRow[] {
  const rows: UserMembershipRow[] = [];
  const restrictId = options?.restrictToOrganizationId;

  for (const user of users) {
    if (isPlatformAdminUser(user)) {
      if (restrictId != null) continue;
      rows.push({
        rowKey: `${user.id}:platform-admin`,
        userId: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        organizationId: null,
        organizationName: '',
        orgRole: 'admin',
        isPlatformAdmin: true,
      });
      continue;
    }

    const memberships = user.memberships ?? [];
    if (memberships.length === 0) {
      const orgName = user.organization.trim();
      if (!orgName) continue;
      if (
        restrictId != null &&
        user.activeOrganizationId != null &&
        user.activeOrganizationId !== restrictId
      ) {
        continue;
      }
      rows.push({
        rowKey: `${user.id}:legacy-${orgName.toLowerCase()}`,
        userId: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        organizationId: user.activeOrganizationId ?? null,
        organizationName: orgName,
        orgRole:
          user.platformRole === 'admin' ? 'developer' : user.platformRole,
        isPlatformAdmin: false,
      });
      continue;
    }

    for (const membership of memberships) {
      if (restrictId != null && membership.organizationId !== restrictId) {
        continue;
      }
      rows.push({
        rowKey: `${user.id}:${membership.organizationId}`,
        userId: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        organizationId: membership.organizationId,
        organizationName: membership.organizationName,
        orgRole: membership.orgRole,
        isPlatformAdmin: false,
      });
    }
  }

  return rows.sort((a, b) => {
    const byName = formatUserDisplayName(a.firstName, a.lastName).localeCompare(
      formatUserDisplayName(b.firstName, b.lastName)
    );
    if (byName !== 0) return byName;
    return a.organizationName.localeCompare(b.organizationName);
  });
}
